import React, { useEffect, useRef, useState } from 'react';
import EditorJS, { OutputData } from '@editorjs/editorjs';

// Editor.js Tools
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import Quote from '@editorjs/quote';
import Delimiter from '@editorjs/delimiter';
import Code from '@editorjs/code';
import InlineCode from '@editorjs/inline-code';
import Warning from '@editorjs/warning';
import ImageTool from '@editorjs/image';
import Checklist from '@editorjs/checklist';


// Helper to convert a File to base64
async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result.toString());
      } else {
        reject(new Error('File could not be read'));
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

type RichTextEditorProps = {
  initialData?: OutputData;   // Editor.js type for saved data
  onChange: (data: OutputData) => void;
};

// Enhanced tooltip component
const Tooltip: React.FC<{show: boolean, text: string}> = ({show, text}) => {
  if (!show) return null;
  
  return (
    <div style={{
      position: 'absolute',
      bottom: '-28px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#333',
      color: 'white',
      padding: '3px 6px',
      borderRadius: '3px',
      fontSize: '11px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: 0.95,
    }}>
      {text}
      <div style={{
        position: 'absolute',
        top: '-4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        borderBottom: '4px solid #333',
      }} />
    </div>
  );
};

// Enhanced toolbar button interface
interface ToolbarButtonProps {
  icon: string;          // SVG icon string
  title: string;         // Button tooltip/title
  onClick: () => void;   // Click handler
  active?: boolean;      // Is this button active?
}

// Enhanced toolbar button component
const ToolbarButton: React.FC<ToolbarButtonProps> = ({ 
  icon, 
  title, 
  onClick, 
  active = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Handle tooltip display with delay
  const handleMouseEnter = () => {
    setIsHovered(true);
    tooltipTimer.current = setTimeout(() => {
      setShowTooltip(true);
    }, 400); // Show tooltip after 400ms hover (faster)
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
  };
  
  return (
    <div
      style={{
        position: 'relative',
        margin: '0 1px',
      }}
    >
      <button
        type="button"
        aria-label={title}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`toolbar-btn ${active ? 'active' : ''}`}
        style={{
          background: active ? '#e0e8ff' : isHovered ? '#ebedf0' : 'transparent',
          border: active ? '1px solid #bdd0ff' : '1px solid transparent',
          padding: '4px 6px',
          borderRadius: '3px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s ease',
          boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <span
          className="icon"
          dangerouslySetInnerHTML={{ __html: icon }}
          style={{
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: active ? '#2563eb' : isHovered ? '#333' : '#555',
            transition: 'color 0.15s ease',
          }}
        />
      </button>
      <Tooltip show={showTooltip} text={title} />
    </div>
  );
};

// Separator component for toolbar - thicker and more visible
const ToolbarSeparator = () => (
  <div style={{
    width: '1px',
    height: '20px',
    backgroundColor: '#d1d5db',
    margin: '0 4px',
  }} />
);

// Group component for toolbar buttons
const ToolbarGroup: React.FC<{children: React.ReactNode}> = ({ children }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    marginRight: '4px', 
  }}>
    {children}
  </div>
);

// Main RichTextEditor component
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialData,
  onChange,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const editorInstance = useRef<EditorJS | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [activeBlock, setActiveBlock] = useState<string>('paragraph');

  // Enhanced icons with better SVGs (slightly smaller)
  const icons = {
    paragraph: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M3 12h18M3 18h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    header: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12V5M4 12V19M4 12H20M20 5V12M20 12V19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    unorderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    orderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 16h2M4 10h2v2H4v2h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    checklist: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 6h11M9 12h11M9 18h11M5 6l-2 2 2 2M5 12l-2 2 2 2M5 18l-2 2 2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    image: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z M8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    quote: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 21c3.33-3.33 5-6 5-8 0-2-.67-3-2-3-.88 0-1.6.67-1.6 1.5 0 .83.72 1.5 1.6 1.5m8 8c3.33-3.33 5-6 5-8 0-2-.67-3-2-3-.88 0-1.6.67-1.6 1.5 0 .83.72 1.5 1.6 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    code: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    table: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    warning: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8v4m0 4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    delimiter: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    inlineCode: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 16l-6-6 6-6M14 8l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  useEffect(() => {
    if (!editorRef.current) return;

    // Initialize Editor.js only once
    if (editorInstance.current) return;

    const initEditor = async () => {
      try {
        if (!editorRef.current) return;
        
        editorInstance.current = new EditorJS({
          holder: editorRef.current,
          data: initialData || { blocks: [] },
          placeholder: 'Start typing here...',
          onReady: () => {
            setIsEditorReady(true);
          },
          async onChange() {
            if (!editorInstance.current) return;
            try {
              const savedData = await editorInstance.current.save();
              onChange(savedData);
            } catch (error) {
              console.error('Error saving Editor.js data:', error);
            }
          },

          tools: {
            header: { class: Header as any, inlineToolbar: true },
            paragraph: { class: Paragraph as any, inlineToolbar: true },
            list: { class: List as any, inlineToolbar: true },
            checklist: { class: Checklist as any, inlineToolbar: true }, 
            orderedList: { 
              class: List as any, 
              inlineToolbar: true,
              config: {
                defaultStyle: 'ordered'
              }
            },
            embed: { class: Embed as any, inlineToolbar: true },
            table: { class: Table as any, inlineToolbar: true },
            quote: { class: Quote as any, inlineToolbar: true },
            delimiter: Delimiter as any,
            code: { class: Code as any },
            inlineCode: { class: InlineCode as any },
            warning: { class: Warning as any, inlineToolbar: true },
            image: {
              class: ImageTool as any,
              inlineToolbar: true,
              config: {
                uploader: {
                  // 1) Upload by file -> convert to Base64
                  async uploadByFile(file: File) {
                    try {
                      const base64 = await readFileAsBase64(file);
                      return {
                        success: 1,
                        file: {
                          url: base64, // store the entire file as base64
                        },
                      };
                    } catch (err) {
                      console.error(err);
                      return {
                        success: 0,
                        message: 'Failed to convert file to base64',
                      };
                    }
                  },

                  // 2) Upload by URL -> fetch and convert to Base64
                  async uploadByUrl(url: string) {
                    try {
                      // fetch the image as a blob
                      const response = await fetch(url);
                      const blob = await response.blob();
                      // convert that blob to a File
                      const file = new File([blob], 'image', { type: blob.type });
                      // convert file to base64
                      const base64 = await readFileAsBase64(file);

                      return {
                        success: 1,
                        file: {
                          url: base64,
                        },
                      };
                    } catch (err) {
                      console.error(err);
                      return {
                        success: 0,
                        message: 'Failed to convert image URL to base64',
                      };
                    }
                  },
                },
              },
            },
          },
        });

        // Set up listener for block changes
        editorInstance.current.on('block-changed', () => {
          updateToolbarState();
        });

      } catch (error) {
        console.error('Error initializing editor:', error);
      }
    };

    initEditor();

    return () => {
      // Cleanup on unmount
      try {
        editorInstance.current?.destroy();
        editorInstance.current = null;
      } catch (error) {
        console.error('Error destroying editor:', error);
      }
    };
  }, []);

  // Function to update toolbar state based on current block
  const updateToolbarState = () => {
    if (!editorInstance.current) return;
    
    try {
      // Get the current block index
      const currentBlockIndex = editorInstance.current.blocks.getCurrentBlockIndex();
      // Get the current block details
      const currentBlock = editorInstance.current.blocks.getBlockByIndex(currentBlockIndex);
      if (currentBlock) {
        setActiveBlock(currentBlock.name);
      }
    } catch (error) {
      console.error('Error updating toolbar state:', error);
    }
  };

  // Function to insert a new block
  const insertBlock = async (type: string) => {
    if (!editorInstance.current) return;
    
    await editorInstance.current.blocks.insert(type);
    setActiveBlock(type);
  };

  // Render the editor with custom toolbar
  return (
    <div
      className="rich-text-editor"
      style={{
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid #dce0e5',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Custom Toolbar - Show when editor is ready */}
      {(isEditorReady || editorInstance.current) && (
        <div
          className="editor-toolbar"
          style={{
            display: 'flex',
            padding: '6px 8px',
            borderBottom: '1px solid #dce0e5',
            backgroundColor: '#f5f7fa',
            borderRadius: '5px 5px 0 0',
            flexWrap: 'wrap',
            position: 'relative',
          }}
        >
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.paragraph}
              title="Paragraph"
              onClick={() => insertBlock('paragraph')}
              active={activeBlock === 'paragraph'}
            />
            <ToolbarButton
              icon={icons.header}
              title="Heading"
              onClick={() => insertBlock('header')}
              active={activeBlock === 'header'}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.unorderedList}
              title="Bullet List"
              onClick={() => insertBlock('list')}
              active={activeBlock === 'list'}
            />
            <ToolbarButton
              icon={icons.orderedList}
              title="Numbered List"
              onClick={() => insertBlock('orderedList')}
              active={activeBlock === 'orderedList'}
            />
            <ToolbarButton
              icon={icons.checklist}
              title="Checklist"
              onClick={() => insertBlock('checklist')}
              active={activeBlock === 'checklist'}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.image}
              title="Image"
              onClick={() => insertBlock('image')}
              active={activeBlock === 'image'}
            />
            <ToolbarButton
              icon={icons.quote}
              title="Quote"
              onClick={() => insertBlock('quote')}
              active={activeBlock === 'quote'}
            />
            <ToolbarButton
              icon={icons.code}
              title="Code Block"
              onClick={() => insertBlock('code')}
              active={activeBlock === 'code'}
            />
            <ToolbarButton
              icon={icons.inlineCode}
              title="Inline Code"
              onClick={() => insertBlock('inlineCode')}
              active={activeBlock === 'inlineCode'}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.table}
              title="Table"
              onClick={() => insertBlock('table')}
              active={activeBlock === 'table'}
            />
            <ToolbarButton
              icon={icons.warning}
              title="Warning"
              onClick={() => insertBlock('warning')}
              active={activeBlock === 'warning'}
            />
            <ToolbarButton
              icon={icons.delimiter}
              title="Delimiter"
              onClick={() => insertBlock('delimiter')}
              active={activeBlock === 'delimiter'}
            />
          </ToolbarGroup>
        </div>
      )}
      
      {/* Editor Container */}
      <div
        style={{
          padding: '16px 12px',
          minHeight: 180,
        }}
        ref={editorRef}
      />
    </div>
  );
};

export default RichTextEditor;